import { defineCopy } from '@/copy';

/** studio strings. Keep the voice: short, funny, no em dashes. Placeholders use {name}. */
export const copy = defineCopy('studio', {
  entry: {
    title: 'Card studio',
    line: 'Turn a full body photo into a pro card.',
    open: 'Open the studio',
    make: 'Make my card',
    left: '{left} of {max} left today.',
    none: 'No card art yet. The mannequin is filling in.',
    have: 'Your card has art. Make fresh art any time.',
    cooking: 'A card is in the oven.',
  },
  studio: {
    title: 'Card studio',
    line: "Upload a full body photo. We'll add the six inches.",
    yourCard: 'Your card',
    newArt: 'New card art',
    gallery: 'Your drops',
    galleryEmpty: {
      title: 'Nothing in the tunnel yet',
      body: 'Take one photo, head to toe. We do the rest.',
    },
    setCard: 'Set as my card',
    onCard: 'On your card',
    clearCard: 'Back to the mannequin',
    pin: 'Pin',
    unpin: 'Unpin',
    hide: 'Hide',
    unhide: 'Unhide',
    delete: 'Delete',
    deleteTitle: 'Delete this card?',
    deleteBody: 'The art and its files go for good.',
    deleteGo: 'Delete it',
    keep: 'Keep it',
    pinned: 'Pinned',
    hidden: 'Hidden',
    cooking: 'In the oven',
    openJob: 'Watch it cook',
    retryCut: 'Retry cutout',
    madeBy: 'Made by a friend',
    usage: '{left} of {max} left today.',
    usageOut: '{max} of {max} today. Ice it and come back tomorrow.',
    budget: "We've spent this month's AI budget. Back on the 1st.",
    mockNote: 'Practice mode: the art is a mannequin until the real studio key is in.',
    shareTitle: 'Show the crew',
    forFriend: 'Card for {name}',
    privacy: {
      title: 'Your photos',
      body: 'Photos stay on our server until you delete them or upload new ones. We keep your last 2 body photos and 2 face photos, and your last 8 cards.',
      wipe: 'Delete my photos and portraits',
      wipeTitle: 'Wipe everything?',
      wipeBody: 'Every photo, card and file of yours goes today. Your stats stay.',
      wipeGo: 'Wipe it all',
      wiped: 'All gone. The mannequin is back.',
    },
  },
  tips: {
    title: 'How to shoot it',
    open: 'Tips',
    items: [
      'Get a teammate to shoot it. Phone at hip height, 2 to 3 big steps away.',
      'Head to toe in the frame. Shoes count.',
      'Plain wall, good light. Face the window, not the lamp.',
      'Stand tall, arms a little away from your body, hands visible.',
      'Wear the fit you want on your card. No hat pulled low, no sunglasses.',
      'Solo? Prop the phone up and use the 10 second timer.',
    ],
    go: 'Got it',
  },
  newJob: {
    title: 'New card',
    line: 'Head to toe, good light, no mirror selfies.',
    stepBody: 'Body photo',
    stepFace: 'Face close-up',
    stepStyle: 'Pick a style',
    forFriend: 'Only shoot {name} if {name} is cool with it. They can hide or delete anything you make.',
    noConsent: '{name} has not switched on friend cards. Ask them to flip it on their profile.',
    notReady: 'Hold on, pulling up the roster.',
  },
  source: {
    title: 'Your body photo',
    camera: 'Shoot it here',
    cameraApp: 'Use the camera app',
    pick: 'Pick from photos',
    reading: 'Reading the photo...',
    benched: "Camera's benched. Use your camera app instead.",
  },
  camera: {
    title: 'Line yourself up',
    label: 'Line yourself up. Head here, feet here.',
    faceLabel: 'Fill the oval with your face.',
    shoot: 'Shoot',
    flip: 'Flip camera',
    timer: 'Timer',
    timerOff: 'Off',
    timerSeconds: '{n} s',
    cancel: 'Back',
    starting: 'Waking the camera up...',
    retake: 'Retake',
    rotate: 'Rotate',
    use: 'Use this one',
    sideways: 'Sideways? Hit Rotate.',
    count: 'Get in position',
  },
  fit: {
    title: 'Fit the frame',
    line: 'Drag the top line to the top of your head and the bottom line to the soles of your shoes.',
    head: 'Head line',
    feet: 'Feet line',
    middle: 'Middle line',
    hint: 'Slide the middle line over your body so the crop is centered.',
    next: 'Looks right',
    redo: 'Pick another photo',
    crop: 'This part gets cropped in.',
  },
  face: {
    title: 'Add a face close-up',
    line: 'Add a face close-up. Your face is tiny in a full body shot and the AI needs a better look.',
    strong: 'Strongly recommended for this photo.',
    camera: 'Shoot it here',
    pick: 'Pick from photos',
    skip: 'Skip it',
    cropTitle: 'Fit your face',
    cropLine: 'Move and zoom until your face fills the oval.',
    zoom: 'Zoom',
    next: 'Use this face',
    tooSmall: 'That one is too small to use. Skipping the close-up.',
  },
  consent: {
    title: 'Before we draw you',
    body: "Your photo goes to our AI provider ({provider}) to draw your card, and nowhere else. We don't let them keep it. Your photos stay on our server until you delete them or upload new ones. Settings has a 'Delete my photos and portraits' button that wipes everything in one tap.",
    mockBody:
      'Practice mode is on, so nothing leaves the server. When the real studio is on, your photo goes to the AI provider to draw your card, and nowhere else.',
    check: 'I am fine with that',
    go: 'Continue',
    providerFal: 'fal',
    providerOpenai: 'OpenAI',
    providerOther: 'our AI provider',
  },
  options: {
    title: 'Pick a style',
    preset: 'Preset',
    funny: 'Attitude',
    funnyLabels: ['Pro', 'Swagger', 'Hype', 'Main character'],
    funnyHints: [
      'Calm, focused, serious pro.',
      'Cocky half smile.',
      'Big grin, chest out.',
      'Action movie poster. The joke is the drama.',
    ],
    clothing: 'Outfit',
    keep: 'Keep my fit',
    jersey: 'Suit me up',
    pose: 'Pose',
    hero: 'Hero pose',
    mine: 'My pose',
    look: 'Look',
    toon: 'Toon',
    sticker: 'Glossy',
    jerseyHelp: 'A blank jersey in the crew colors.',
    go: 'Draw my card',
    working: 'Sending it up...',
    saveDev: 'Save processed photos (dev)',
    sendFailed: "That didn't go up. Check the signal and try again.",
    checksTitle: 'Heads up',
    presets: {
      rookie_card: 'Rookie Card',
      streetball: 'Streetball',
      game_cover: 'Game Cover',
    },
    presetBlurbs: {
      rookie_card: 'Hip ball, proud stance.',
      streetball: 'Ball under the arm, pointing at you.',
      game_cover: 'Power stance, palm it at the camera.',
    },
  },
  checks: {
    dark: "It's dark in here. Find a window.",
    bright: 'Too bright. Step out of the sun.',
    blurry: 'Bit blurry. Hold still and try again?',
    smallBody: 'Small photo. The card will be a little soft.',
    smallFace: 'The face is tiny in this shot.',
    rotated: 'Turned it upright for you.',
  },
  errors: {
    decode: "Can't read that file. Send a JPG or screenshot it.",
    landscape: 'Turn the phone upright. We need you head to toe.',
    tooSmall: 'Too small to scout. Use the original photo, not a thumbnail.',
    tooClose: "You're too close to the edge. Step back and shoot again.",
    bodyTiny: 'You are too small in the frame. Step closer or crop tighter.',
    notEncoded: "This phone can't make a JPEG. Try the camera app.",
    daily: '{max} of {max} today. Ice it and come back tomorrow.',
    budget: "We've spent this month's AI budget. Back on the 1st.",
    noConsent: 'They have not switched on friend cards.',
    aiOff: 'The studio is closed for now.',
    generic: 'Something fumbled. Try again.',
  },
  job: {
    title: 'Your card',
    loading: 'Looking for your card...',
    notFound: {
      title: 'No card here',
      body: 'That job is gone or was never yours.',
      action: 'Back to the studio',
    },
    queued: "In the tunnel. You're #{pos} in line.",
    queuedFirst: "In the tunnel. You're next.",
    editing: 'Drawing you six inches taller.',
    cutting: 'Cutting you out. Hold still.',
    done: 'Fresh card just dropped.',
    doneHint: 'Set it, tweak it or run it back.',
    underAMinute: 'Usually under a minute.',
    cancel: 'Call it off',
    canceled: 'Called off. Your credit is back.',
    setCard: 'Set as my card',
    setDone: 'On your card now.',
    runItBack: 'Run it back',
    fixFit: 'Fix the fit',
    share: 'Share',
    retryCut: 'Retry cutout',
    cutFailed: "Couldn't cut you out clean. Retry cutout?",
    cutFailedNote: 'The card shows the art in the window until then.',
    edge: 'Your feet got cut off. Run it back?',
    blocked: 'The ref blew the whistle on that one. Try another photo or keep your own fit.',
    failed: "That one didn't make the cut. Your credit's back.",
    failedBilled: "That one didn't make the cut. The provider still billed it.",
    timeout: 'That took too long. Run it back?',
    auth: 'The studio key is not working. Tell the owner.',
    back: 'Back to the studio',
    friendNote: 'Made for {name}. They decide if it goes on their card.',
    progressLabel: 'Card progress',
    steps: ['Queued', 'Drawing', 'Cutting', 'Done'],
    daily: '{max} of {max} today. Ice it and come back tomorrow.',
    budget: "We've spent this month's AI budget. Back on the 1st.",
  },
  nudge: {
    title: 'Fix the fit',
    line: 'Move the figure a hair or resize it. The head never goes over the card edge.',
    up: 'Height',
    scale: 'Size',
    reset: 'Reset',
    save: 'Save',
    saved: 'Fit saved.',
  },
});
